import { useEffect, useMemo, useState } from "react";

/**
 * BuyingPowerGauge
 * ----------------
 * Semi-circle gauge showing a buyer's qualifying price range with a needle
 * pointing to their target listing's position (or the sweet-spot midpoint
 * when no listing is provided).
 *
 * States handled:
 *   - no listing          → needle at midpoint, callout "Sweet spot"
 *   - listing in range    → needle at that %, callout = listing price
 *   - listing below range → needle at MIN with left-pointing "Below" chip
 *   - listing above range → needle at MAX with right-pointing "Above" chip
 */
export default function BuyingPowerGauge({
  low = 0,
  high = 0,
  listingPrice = null,
  matchStatus = "match",
  size = 320,
}) {
  const fmt = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const safeLow = Math.max(0, Number(low) || 0);
  const safeHigh = Math.max(safeLow + 1, Number(high) || 0);
  const mid = (safeLow + safeHigh) / 2;

  const hasListing = Number.isFinite(Number(listingPrice)) && Number(listingPrice) > 0;
  const rawTarget = hasListing ? Number(listingPrice) : mid;
  const isBelow = hasListing && rawTarget < safeLow;
  const isAbove = hasListing && rawTarget > safeHigh;

  const clamped = Math.max(safeLow, Math.min(safeHigh, rawTarget));
  const t = (clamped - safeLow) / (safeHigh - safeLow); // 0..1

  // Needle sweep animation
  const [angle, setAngle] = useState(-90);
  useEffect(() => {
    setAngle(-90);
    const id = requestAnimationFrame(() => {
      setTimeout(() => setAngle(-90 + 180 * t), 60);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const W = size;
  const H = Math.round(size * 0.66);
  const cx = W / 2;
  const cy = H - 10;
  const R = W / 2 - 24;
  const thickness = 22;

  const polar = (deg, r) => {
    const rad = (Math.PI / 180) * deg;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arcPath = (startDeg, endDeg, r) => {
    const s = polar(startDeg, r);
    const e = polar(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const needleDeg = 180 + 180 * ((angle + 90) / 180);
  const needleLen = R - 4;
  const needlePoint = polar(needleDeg, needleLen);

  // Status chip / semantics
  const statusMeta = useMemo(() => {
    if (isBelow) {
      return {
        color: "#06B6D4",
        label: "Below your range",
        sub: "You qualify for more than this",
      };
    }
    if (isAbove) {
      return {
        color: "#F43F5E",
        label: "Above your range",
        sub: "A stretch beyond your current qualifying range",
      };
    }
    if (matchStatus === "not_yet") {
      return { color: "#F59E0B", label: "Keep saving", sub: "You're almost there" };
    }
    if (matchStatus === "stretch") {
      return { color: "#F59E0B", label: "Stretch zone", sub: "On the edge of your range" };
    }
    if (!hasListing) {
      return { color: "#52BFB8", label: "Sweet spot", sub: "The middle of your range" };
    }
    if (t < 0.2) return { color: "#52BFB8", label: "Comfortable", sub: "Well within reach" };
    if (t > 0.8) return { color: "#F59E0B", label: "Stretch zone", sub: "Top of your range" };
    return { color: "#10B981", label: "Sweet spot", sub: "Right in the sweet spot" };
  }, [isBelow, isAbove, matchStatus, hasListing, t]);

  // Below/above showcase a side pill rather than a centered callout
  const sideBadge = isBelow ? "left" : isAbove ? "right" : null;

  return (
    <div className="w-full flex flex-col items-center" data-testid="buying-power-gauge">
      {/* Status chip */}
      <div
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold mb-4 uppercase tracking-wider"
        style={{
          color: statusMeta.color,
          backgroundColor: `${statusMeta.color}15`,
          border: `1px solid ${statusMeta.color}30`,
        }}
        data-testid="gauge-status-chip"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusMeta.color }} />
        {statusMeta.label}
      </div>

      <div className="relative" style={{ width: W, height: H + 10 }}>
        <svg
          width={W}
          height={H + 10}
          viewBox={`0 0 ${W} ${H + 10}`}
          className="overflow-visible"
          aria-label="Buying power range gauge"
        >
          <defs>
            <radialGradient id="bpg-glow" cx="50%" cy="100%" r="80%">
              <stop offset="0%" stopColor="#52BFB8" stopOpacity="0.35" />
              <stop offset="60%" stopColor="#52BFB8" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#52BFB8" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="bpg-arc" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#52BFB8" />
              <stop offset="50%" stopColor="#10B981" />
              <stop offset="85%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="bpg-needle" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2D3748" />
              <stop offset="100%" stopColor={statusMeta.color} />
            </linearGradient>
            <filter id="bpg-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2.4" />
              <feOffset dx="0" dy="2" result="offsetblur" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.35" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Glow halo */}
          <circle cx={cx} cy={cy} r={R + 14} fill="url(#bpg-glow)" />

          {/* Base track */}
          <path
            d={arcPath(180, 360, R)}
            stroke="#EEF2F6"
            strokeWidth={thickness + 2}
            strokeLinecap="round"
            fill="none"
          />
          {/* Full gradient arc — always visible as the range reference */}
          <path
            d={arcPath(180, 360, R)}
            stroke="url(#bpg-arc)"
            strokeWidth={thickness}
            strokeLinecap="round"
            fill="none"
            opacity={0.95}
          />
          {/* Soft inner highlight to add depth */}
          <path
            d={arcPath(182, 358, R)}
            stroke="rgba(255,255,255,0.35)"
            strokeWidth={2}
            strokeLinecap="round"
            fill="none"
          />

          {/* Zone dividers */}
          {[0.2, 0.8].map((p, i) => {
            const deg = 180 + 180 * p;
            const inner = polar(deg, R - thickness / 2 - 4);
            const outer = polar(deg, R + thickness / 2 + 4);
            return (
              <line
                key={i}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke="#CBD5E1"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            );
          })}

          {/* Needle */}
          <g filter="url(#bpg-shadow)">
            <line
              x1={cx}
              y1={cy}
              x2={needlePoint.x}
              y2={needlePoint.y}
              stroke="url(#bpg-needle)"
              strokeWidth="3.5"
              strokeLinecap="round"
              style={{ transition: "all 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}
            />
            <circle cx={cx} cy={cy} r="12" fill="#0F172A" />
            <circle cx={cx} cy={cy} r="5" fill={statusMeta.color} />
          </g>

          {/* Base scale ticks: MIN, MAX only (sweet spot label handled below SVG) */}
          <text
            x={polar(180, R).x}
            y={polar(180, R).y + 32}
            fontSize="9"
            fill="#94A3B8"
            textAnchor="middle"
            fontWeight="700"
            letterSpacing="1"
          >
            MIN
          </text>
          <text
            x={polar(360, R).x}
            y={polar(360, R).y + 32}
            fontSize="9"
            fill="#94A3B8"
            textAnchor="middle"
            fontWeight="700"
            letterSpacing="1"
          >
            MAX
          </text>
        </svg>

        {/* Centered callout — floats above the hub */}
        <div
          className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none"
          style={{ top: Math.max(20, cy - R * 0.72) }}
        >
          <div className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">
            {isBelow || isAbove ? "YOUR LISTING" : hasListing ? "YOUR TARGET" : "SWEET SPOT"}
          </div>
          <div
            className="text-2xl sm:text-3xl font-bold font-cabinet leading-tight mt-0.5"
            style={{ color: statusMeta.color }}
            data-testid="gauge-pointer-value"
          >
            {fmt(hasListing ? rawTarget : mid)}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">
            {statusMeta.sub}
          </div>
        </div>

        {/* Below/above overflow indicator (arrow outside the arc) */}
        {sideBadge && (
          <div
            className={`absolute flex items-center gap-1 px-2 py-1 rounded-full bg-white border shadow-md text-[10px] font-semibold uppercase tracking-wider ${
              sideBadge === "left" ? "left-[-6px]" : "right-[-6px]"
            }`}
            style={{
              top: cy - 14,
              color: statusMeta.color,
              borderColor: `${statusMeta.color}50`,
            }}
          >
            {sideBadge === "left" ? "◂" : "▸"} off scale
          </div>
        )}
      </div>

      {/* Range endpoints with gradient connector */}
      <div className="w-full flex items-stretch justify-between mt-5">
        <RangePill label="Comfortable buy" value={fmt(safeLow)} tone="teal" active={!isAbove && t < 0.5} />
        <div className="flex-1 flex items-center px-2 sm:px-3">
          <div className="w-full h-[3px] rounded-full bg-gradient-to-r from-[#52BFB8] via-emerald-400 to-amber-400 relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#52BFB8] via-emerald-400 to-amber-400 blur-[6px] opacity-60" />
          </div>
        </div>
        <RangePill label="Max stretch" value={fmt(safeHigh)} tone="amber" active={!isBelow && t >= 0.5} />
      </div>
    </div>
  );
}

function RangePill({ label, value, tone, active }) {
  const map = {
    teal: { ring: "ring-[#52BFB8]/40", dot: "bg-[#52BFB8]", chip: "bg-[#52BFB8]/5" },
    amber: { ring: "ring-amber-400/50", dot: "bg-amber-500", chip: "bg-amber-50" },
  };
  const t = map[tone] || map.teal;
  return (
    <div
      className={`flex items-center gap-2 rounded-xl px-3 py-2 ring-1 bg-white ${
        active ? `${t.ring} shadow-sm` : "ring-gray-200"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      <div className="leading-tight">
        <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold">
          {label}
        </div>
        <div className="text-sm font-bold text-[#2D3748] font-cabinet">{value}</div>
      </div>
    </div>
  );
}

function ArcReveal({ d, length, progress, thickness }) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setTimeout(() => setP(progress), 80));
    return () => cancelAnimationFrame(raf);
  }, [progress]);
  return (
    <path
      d={d}
      stroke="url(#bpg-arc)"
      strokeWidth={thickness}
      strokeLinecap="round"
      fill="none"
      strokeDasharray={`${length} ${length}`}
      strokeDashoffset={length - length * p}
      style={{ transition: "stroke-dashoffset 1400ms cubic-bezier(0.22, 1, 0.36, 1)" }}
    />
  );
}
